import { memo, useMemo } from 'react';
import { cn, createIconElement } from '@/lib';
import type { AnalyticsMetricCardProps } from '../types/analytics.types';
import IcTrendUp from '@/assets/icons/ic-trend-up.svg';
import IcTrendDown from '@/assets/icons/ic-trend-down.svg';

function AnalyticsMetricCardComponent({
    metric,
    active,
    loading,
    onClick,
    className = '',
    prefix = '',
    highlightColor,
    keepStylingSameOnMobile = false,
}: AnalyticsMetricCardProps) {
    // Memoize trend calculations to prevent unnecessary re-computations
    const trendData = useMemo(() => {
        const isPositiveTrend = metric.trend >= 0;
        return {
            trendPercentage: Math.abs(metric.trend * 100).toFixed(0),
            isPositiveTrend,
            trendColor: isPositiveTrend ? 'text-success-main' : 'text-gray',
            TrendIcon: isPositiveTrend ? IcTrendUp : IcTrendDown,
        };
    }, [metric.trend]);

    return (
        <button
            onClick={onClick}
            className={cn(
                'group flex h-full w-full flex-1 cursor-pointer flex-col justify-start rounded-lg px-0 transition-all hover:border-[var(--highlight-color)]/50 lg:px-6 lg:py-4',
                active ? `!border-[var(--highlight-color)] lg:border` : 'border-transparent lg:border',
                className
            )}
            style={
                {
                    '--highlight-color': highlightColor,
                } as React.CSSProperties
            }
        >
            <div className='flex items-center justify-between'>
                <div className='flex items-center gap-1'>
                    {createIconElement(metric.icon, { className: 'size-5 text-gray' })}
                    <span className='label-sm-bold text-gray whitespace-nowrap'>{metric.title}</span>
                    <div
                        className={cn(
                            'duration-fast block size-4 rounded-full transition-colors group-hover:bg-[var(--highlight-color)]/50 lg:hidden',
                            active ? `!bg-[var(--highlight-color)]` : 'bg-transparent'
                        )}
                    />
                </div>
            </div>

            <div className='flex items-end gap-2 pt-2'>
                {loading ? (
                    <div className='bg-neutral-subtle h-8 w-24 animate-pulse rounded' />
                ) : (
                    <>
                        <p
                            className={`text-dark text-heading-h2 md:text-heading-h1 ${keepStylingSameOnMobile ? 'max-md:text-heading-h1' : ''}`}
                        >
                            {prefix}
                            {Math.round(metric.value).toLocaleString()}
                        </p>

                        {metric.trend !== 0 && (
                            <div className='relative -top-1 flex items-center'>
                                {createIconElement(trendData.TrendIcon, {
                                    className: `size-5 mr-1 ${trendData.trendColor}`,
                                })}
                                <span className='body-2-bold'>{trendData.trendPercentage}%</span>
                            </div>
                        )}
                    </>
                )}
            </div>
        </button>
    );
}

/**
 * Memoized AnalyticsMetricCard to prevent unnecessary re-renders
 * Only re-renders when props actually change
 */
export const AnalyticsMetricCard = memo(AnalyticsMetricCardComponent);
